import React from "react";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

function Privacy() {
  const { brandName, legalName, contact } = siteConfig;
  const sections = [
    ["overview", "Overview"],
    ["information", "Information we collect"],
    ["use", "How we use information"],
    ["cookies", "Cookies & analytics"],
    ["sharing", "Sharing information"],
    ["retention", "Retention & security"],
    ["choices", "Your choices"],
    ["contact-privacy", "Contact"],
  ];

  return (
    <main className="privacy" id="main-content" tabIndex="-1">
      <header className="privacy__header">
        <p>Legal / 01</p>
        <h1>Privacy<br />policy</h1>
        <div>
          <span>Last updated</span>
          <time dateTime="2026-09-16">16 September 2026</time>
        </div>
      </header>

      <aside className="privacy__demo-note" role="note">
        <strong>Demonstration notice</strong>
        <p>
          This is original sample copy for a front-end demonstration. It is not
          legal advice and must be reviewed and replaced before a live store
          collects personal information.
        </p>
      </aside>

      <div className="privacy__layout">
        <nav className="privacy__nav" aria-label="Privacy policy sections">
          <p>On this page</p>
          <ol>
            {sections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="privacy__article">
          <section id="overview">
            <p className="privacy__section-number">01 / Overview</p>
            <h2>A clear view of your data.</h2>
            <p>
              This sample policy explains how {legalName}, trading as {brandName},
              would collect, use and protect personal information when you browse
              this storefront, create an account, place an order or contact us.
            </p>
            <p>
              In a production store, the final policy should identify the data
              controller, all connected service providers and the privacy rules
              that apply in every region where the store operates.
            </p>
          </section>

          <section id="information">
            <p className="privacy__section-number">02 / Information we collect</p>
            <h2>Information you share and information created by your visit.</h2>
            <h3>Details you provide</h3>
            <ul>
              <li>Name, email address, telephone number and delivery address.</li>
              <li>Order selections, sizing choices, returns and support messages.</li>
              <li>Account preferences, saved products and newsletter choices.</li>
              <li>
                Payment status and billing details supplied by a secure payment
                provider; a live store should not retain full card numbers.
              </li>
            </ul>
            <h3>Details collected automatically</h3>
            <p>
              A connected commerce service may receive device type, browser,
              approximate location, IP address, referral page and interactions
              such as viewed products or checkout progress. The exact list must
              match the analytics and hosting tools used in production.
            </p>
          </section>

          <section id="use">
            <p className="privacy__section-number">03 / How we use information</p>
            <h2>Only for a stated purpose.</h2>
            <p>Personal information may be used to:</p>
            <ul>
              <li>process, deliver, support and refund purchases;</li>
              <li>maintain account, cart and wishlist preferences;</li>
              <li>respond to product, sizing and service questions;</li>
              <li>prevent fraudulent, abusive or insecure activity;</li>
              <li>improve navigation, product presentation and store performance;</li>
              <li>
                send marketing only when permission or another valid basis exists.
              </li>
            </ul>
            <p>
              Before launch, each purpose should be paired with an appropriate
              legal basis, such as fulfilling a contract, meeting a legal duty,
              consent or a documented legitimate interest.
            </p>
          </section>

          <section id="cookies">
            <p className="privacy__section-number">04 / Cookies &amp; analytics</p>
            <h2>Small files, visible choices.</h2>
            <p>
              Essential storage may keep a cart, remember a region or protect a
              session. Optional analytics and advertising technologies should
              remain disabled until a visitor makes a choice where consent is
              required. A production cookie banner should allow choices to be
              reviewed or withdrawn at any time.
            </p>
            <div className="privacy__data-grid">
              <div>
                <h3>Essential</h3>
                <p>Cart, checkout, security and accessibility preferences.</p>
              </div>
              <div>
                <h3>Measurement</h3>
                <p>Aggregated performance and navigation insights.</p>
              </div>
              <div>
                <h3>Personalisation</h3>
                <p>Saved preferences and relevant product recommendations.</p>
              </div>
            </div>
          </section>

          <section id="sharing">
            <p className="privacy__section-number">05 / Sharing information</p>
            <h2>Limited to what the service needs.</h2>
            <p>
              A live store may share necessary details with commerce hosting,
              payment, fraud prevention, fulfilment, delivery, support, email and
              analytics providers. Each provider should receive only the data
              required for its role and be bound by appropriate safeguards.
            </p>
            <p>
              Personal information should not be sold. It may be disclosed when
              required by law, to protect people or the service, or as part of a
              business reorganisation with suitable notice and protections.
              International transfers require the safeguards applicable to the
              people and regions involved.
            </p>
          </section>

          <section id="retention">
            <p className="privacy__section-number">06 / Retention &amp; security</p>
            <h2>Kept only as long as needed.</h2>
            <p>
              Order and tax records may need to be retained for a legally defined
              period. Support conversations, inactive accounts and marketing data
              should follow shorter documented schedules. When information is no
              longer needed, it should be deleted or irreversibly anonymised.
            </p>
            <p>
              Appropriate technical and organisational controls should protect
              information in transit and at rest. No online service can promise
              absolute security, so a production team must also maintain response
              and notification procedures for potential incidents.
            </p>
          </section>

          <section id="choices">
            <p className="privacy__section-number">07 / Your choices</p>
            <h2>Your information remains yours.</h2>
            <p>
              Depending on local law, you may be able to request access,
              correction, deletion, restriction, portability or an objection to
              certain uses. You may also withdraw consent without changing the
              lawfulness of earlier processing.
            </p>
            <p>
              Identity may need to be verified before a request is completed.
              You can unsubscribe from marketing using the link in an email and
              may have the right to contact your local data protection authority.
            </p>
          </section>

          <section id="contact-privacy">
            <p className="privacy__section-number">08 / Contact</p>
            <h2>Questions about privacy?</h2>
            <p>
              Send a request to <a href={`mailto:${contact.email}`}>{contact.email}</a>
              {" "}and include “Privacy request” in the subject line.
            </p>
            <address>
              {legalName}<br />
              {contact.address}
            </address>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
